// Stub: expo-image-picker — uses browser <input type="file"> on web/desktop.
//
// `<input type="file">` doesn't require any OS-level permission, so we report
// these as "granted" rather than "denied". Returning denied here would force
// every caller to either Platform.OS-guard the permission check or be told
// "permission denied" for an action that has no permission to grant — which is
// exactly what the medication / avatar / vault upload flows were hitting on
// Electron before this change.
const granted = () => Promise.resolve({
  status: 'granted',
  granted: true,
  expires: 'never',
  canAskAgain: false,
  accessPrivileges: 'all',
});

export const getMediaLibraryPermissionsAsync     = granted;
export const requestMediaLibraryPermissionsAsync = granted;
export const getCameraPermissionsAsync           = granted;
export const requestCameraPermissionsAsync       = granted;

// Native expo-image-picker, when called with `base64: true`, returns assets
// shaped like `{ uri, base64, mimeType, width, height, fileName, fileSize }`.
// The stub used to only set `uri` (a data-URL string), so any caller that
// branched on `result.assets[0].base64` (medication image, profile avatar,
// project avatar) silently dropped the upload on web. Mirror the native
// contract by parsing the data URL once and pulling out both the base64
// payload and the MIME type.
function dataUrlToBase64(dataUrl) {
  if (typeof dataUrl !== 'string') return { mime: null, base64: null };
  const match = /^data:([^;,]+)?(?:;[^,]*)?,(.*)$/i.exec(dataUrl);
  if (!match) return { mime: null, base64: null };
  return {
    mime: match[1] || null,
    // FileReader.readAsDataURL always emits base64-encoded URLs, so the
    // payload is the part after the comma. Decoding/re-encoding here would
    // just churn CPU.
    base64: match[2] || null,
  };
}

function readAssetFromFile(file, includeBase64) {
  return new Promise((res) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const uri = e.target?.result;
      const { mime, base64 } = dataUrlToBase64(uri);
      const asset = {
        uri,
        width: 0,
        height: 0,
        type: 'image',
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || mime || 'image/jpeg',
      };
      if (includeBase64) asset.base64 = base64;
      res(asset);
    };
    reader.onerror = () => res(null);
    reader.readAsDataURL(file);
  });
}

export const launchImageLibraryAsync = (options = {}) =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type  = 'file';
    input.accept = options.mediaTypes === 'Videos' ? 'video/*' : 'image/*';
    input.multiple = options.allowsMultipleSelection ?? false;
    input.onchange = () => {
      const files = Array.from(input.files || []);
      if (!files.length) { resolve({ canceled: true, assets: [] }); return; }
      Promise.all(files.map((file) => readAssetFromFile(file, !!options.base64)))
        .then((assets) => {
          const filtered = assets.filter(Boolean);
          if (!filtered.length) { resolve({ canceled: true, assets: [] }); return; }
          resolve({ canceled: false, assets: filtered });
        });
    };
    input.oncancel = () => resolve({ canceled: true, assets: [] });
    input.click();
  });

// `capture="environment"` asks the browser to prefer the rear camera on
// devices that support it (most modern mobile browsers); on desktop browsers
// this attribute is ignored and the user gets the standard file dialog,
// which is the closest analogue to "Take photo" we can offer. Returning
// canceled used to silently swallow taps from the medication / vault flows.
export const launchCameraAsync = (options = {}) =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type  = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'environment');
    input.onchange = () => {
      const files = Array.from(input.files || []);
      if (!files.length) { resolve({ canceled: true, assets: [] }); return; }
      readAssetFromFile(files[0], !!options.base64).then((asset) => {
        if (!asset) { resolve({ canceled: true, assets: [] }); return; }
        resolve({ canceled: false, assets: [asset] });
      });
    };
    input.oncancel = () => resolve({ canceled: true, assets: [] });
    input.click();
  });

export const MediaTypeOptions = { All: 'All', Videos: 'Videos', Images: 'Images' };
export const UIImagePickerControllerQualityType = { High: 0, Medium: 1, Low: 2 };
export const UIImagePickerPresentationStyle = { FullScreen: 'fullScreen', PageSheet: 'pageSheet', FormSheet: 'formSheet', CurrentContext: 'currentContext', OverCurrentContext: 'overCurrentContext', Popover: 'popover', BlurOverFullScreen: 'blurOverFullScreen', Automatic: 'automatic' };

export default {
  getMediaLibraryPermissionsAsync, requestMediaLibraryPermissionsAsync,
  getCameraPermissionsAsync, requestCameraPermissionsAsync,
  launchImageLibraryAsync, launchCameraAsync,
  MediaTypeOptions, UIImagePickerControllerQualityType, UIImagePickerPresentationStyle,
};
